import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Grid } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { Alert } from '@material-ui/lab';

import { getConfig } from '@klpkonsern/appconfig';
import { useCurrentUser } from '@klp/auth';
import { ByggInfoSummary } from '@klp/bygg/components/ByggInfoSummary';
import { ByggRoutes } from '@klp/bygg/config';
import {
    ContractManagementSummary,
    ContractManagementAccess,
} from '@klp/contractmanagement';
import { appSelectors } from '@klp/core/app';
import { useAppParams } from '@klp/core/app/useAppParams';
import { Block, Heading, Link } from '@klp/design';
import { PictogramName } from '@klp/design/Illustrations';
import { FakturaListSummary } from '@klp/faktura/components/FakturaListSummary';
import { FakturaAccess, FakturaRoutes } from '@klp/faktura/config';
import { Main } from '@klp/layout';
import {
    MessageBoardRoutes,
    OppslagstavleAccess,
} from '@klp/messageboard/config';
import {
    MessageListSummary,
    MessageBoardSummary,
} from '@klp/messageboard/views';
import { generateRouteParams } from '@klp/routing';
import { SakForsidefeed } from '@klp/sak/classes';
import { SakListSummary } from '@klp/sak/components';
import { SakAccess, SakRoutes } from '@klp/sak/config';
import { useGetSakForsidefeedQuery } from '@klp/sak/rtkApi';

import { FrontpageCard } from '../components/FrontpageCard';
// import { FrontpageMoteromCalendar } from '../components/FrontpageMoteromCalendar';
import SocialMediaWidget from '../components/SocialMediaWidget';
import { useGetFrontpageQuery } from '../services/rtkApi';
import { TwoFactorInfo } from '../TwoFactorInfo';

interface ILevendeKontrakterParams {
    language: string;
    kundeNummer?: string;
    autologin: boolean;
}

export const Frontpage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const params = useAppParams();
    const { selectedClient, contractPath } = params;

    // App state (for trigger)
    const appState = useSelector(appSelectors);
    const { trigger } = appState;

    const kundeNummer = selectedClient?.kundeNummer || '0';

    const { data: frontpageData, refetch } = useGetFrontpageQuery(kundeNummer);

    const { data: sakForsidefeedResponse } =
        useGetSakForsidefeedQuery(kundeNummer);
    const sakForsidefeedData = sakForsidefeedResponse?.sakForsidefeed;

    const { currentUser: user } = useCurrentUser();

    const [showAlert, setShowAlert] = useState<boolean>(
        !TwoFactorInfo.getTwoFactorAlertReadStatus(),
    );

    // Refetch frontpage data when trigger changes
    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trigger]);

    const handleHideAlert = () => {
        TwoFactorInfo.hideTwoFactorAlert();
        setShowAlert(false);
    };

    const openLEKOHandler = (destination: string) => () => {
        window.open(destination, 'Contracts');
    };

    const showContractCard = (): boolean => {
        return selectedClient === undefined
            ? true
            : (selectedClient?.isHandelKunde &&
                  !selectedClient?.isNaeringKunde) ||
                  false;
    };

    const showMessageBoard = (): boolean => {
        return !showContractCard();
    };

    // Build LEKO URL
    const newTabParams: ILevendeKontrakterParams = {
        language: i18n.language,
        autologin: true,
    };

    if (selectedClient?.kundeNummer) {
        newTabParams.kundeNummer = selectedClient.kundeNummer;
    }

    const lekoURL = getConfig('WEBAPP_CONTRACT_FRAME_URL', '')
        .concat(contractPath || '')
        .concat(generateRouteParams(newTabParams));

    // Extract frontpage data with defaults
    const {
        frontpageMessages = [],
        frontpageFaktura = [],
        frontpageByggInfo = [],
        frontpageAvtaler = 0,
        frontpageSumUtestaaende = 0,
        frontpageUtestaaendeAntall = 0,
    } = frontpageData || {};

    return (
        <Main decorate={true}>
            <Block
                style={{
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                <Heading
                    klpHeadingLevel="ONE"
                    translation={`frontPage.welcome.${getConfig('WEBAPP_APP_NAME', '')}`}
                    align="center"
                />
            </Block>
            <Grid container={true} spacing={2} justifyContent="center">
                <Grid item={true} xs={12} sm={6} md={3}>
                    <Block paddingTop="massive" paddingBottom="bulky">
                        {showAlert && showAlert !== undefined && (
                            <Alert
                                severity="info"
                                color="info"
                                action={
                                    <IconButton
                                        aria-label="close"
                                        color="inherit"
                                        size="small"
                                        onClick={handleHideAlert}
                                    >
                                        <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                }
                            >
                                {t('frontPage.twoFactorAlertText')}
                                <strong>
                                    {t('frontPage.boldTwoFactorAlertText')}
                                </strong>
                            </Alert>
                        )}
                    </Block>
                </Grid>
            </Grid>
            <Grid container={true} spacing={2} justifyContent="center">
                <Grid item={true} xs={12} sm={6} md={3}>
                    <FrontpageCard
                        data-testid="DashboardButtonBygg"
                        title="frontPage.buildings"
                        pictogram={PictogramName.BYGNINGER.EIENDOM}
                        klpPalette="secondary"
                        actionRoute={ByggRoutes.list()}
                        actionText="frontPage.buildingsLink"
                    >
                        <ByggInfoSummary
                            byggInfo={frontpageByggInfo || []}
                            avtaler={frontpageAvtaler}
                        />
                    </FrontpageCard>
                </Grid>

                {FakturaAccess(user) && (
                    <Grid item={true} xs={12} sm={6} md={3}>
                        <FrontpageCard
                            data-testid="DashboardButtonInvoices"
                            title="frontPage.invoice.title"
                            pictogram={
                                PictogramName.BANK_OG_SPARING.FINANSIERINGSBEVIS
                            }
                            klpPalette="secondary"
                            actionRoute={FakturaRoutes.list()}
                            actionText="frontPage.invoice.invoiceLink"
                        >
                            <FakturaListSummary
                                fakturaList={frontpageFaktura || []}
                                sumUtestående={frontpageSumUtestaaende}
                                utestaaendeAntall={frontpageUtestaaendeAntall}
                            />
                        </FrontpageCard>
                    </Grid>
                )}

                {ContractManagementAccess(user) && showContractCard() && (
                    <Grid item={true} xs={12} sm={6} md={3}>
                        <FrontpageCard
                            data-testid="DashboardButtonContract"
                            title="frontPage.contract"
                            pictogram={
                                PictogramName.BANK_OG_SPARING.OPPRETT_KONTO
                            }
                            klpPalette="secondary"
                            actionClickHandler={openLEKOHandler(lekoURL)}
                            actionText="frontPage.contractManagement.contractNegotiation"
                            external={true}
                        >
                            <ContractManagementSummary />
                        </FrontpageCard>
                    </Grid>
                )}

                {OppslagstavleAccess(user) && showMessageBoard() && (
                    <Grid item={true} xs={12} sm={6} md={3}>
                        <FrontpageCard
                            data-testid="DashboardButtonOppslagstavle"
                            title="frontPage.messages"
                            pictogram={
                                PictogramName.BANK_OG_SPARING.OPPRETT_KONTO
                            }
                            klpPalette="secondary"
                            actionRoute={MessageBoardRoutes.list()}
                            actionText="frontPage.messages"
                        >
                            <MessageListSummary
                                messageList={frontpageMessages}
                            />
                        </FrontpageCard>
                    </Grid>
                )}

                {SakAccess(user) && (
                    <Grid item={true} xs={12} sm={6} md={3}>
                        <FrontpageCard
                            data-testid="DashboardButtonMessages"
                            title="superOffice.mytasks"
                            pictogram={PictogramName.VERKTØY.VERKTØYKASSE}
                            klpPalette="secondary"
                            actionRoute={SakRoutes.list()}
                            actionText="frontPage.mytasksLink"
                        >
                            <SakListSummary
                                sakFeed={
                                    sakForsidefeedData || new SakForsidefeed([])
                                }
                            />
                        </FrontpageCard>
                    </Grid>
                )}
            </Grid>

            {/*             <FrontpageMoteromCalendar
                byggNummer={frontpageByggInfo[0]?.byggNummer}
            /> */}

            {getConfig('WEBAPP_TAGGBOX_ENABLE', false) && (
                <Grid container={true} justifyContent="center">
                    <Heading
                        klpHeadingLevel="ONE"
                        translation="frontPage.newsFeed.title"
                        marginTop="massive"
                        klpColor="light"
                        align="center"
                        data-testid="Frontpage__Subtitle_newsfeed"
                    />
                    <SocialMediaWidget />
                    <Grid item={true}>
                        <Link
                            href={getConfig('WEBAPP_LINKEDIN_LINK')}
                            translation="frontPage.newsFeed.linkText"
                            target="_blank"
                        />
                    </Grid>
                </Grid>
            )}

            {OppslagstavleAccess(user) && (
                <Grid>
                    <MessageBoardSummary trigger={trigger} />
                </Grid>
            )}
        </Main>
    );
};

Frontpage.displayName = 'Frontpage';
